import React from 'react';
import { InstagramIcon, MailIcon, MessageCircleIcon, UsersIcon } from 'lucide-react';
import { Container, Section } from '../components/ui/Section';
import { PageHeader } from '../components/ui/PageHeader';
import { ButtonExternal } from '../components/ui/Button';
import { NewTabHint } from '../components/ui/NewTabHint';
import { Reveal, stagger } from '../components/motion/Reveal';
import { DoodleStar } from '../components/brand/Doodles';
import { emailAddress, externalLinks } from '../data/site';
const contactCards = [{
  icon: MailIcon,
  label: 'Email',
  value: emailAddress,
  href: externalLinks.email
}, {
  icon: InstagramIcon,
  label: 'Instagram',
  value: '@ucfbcm',
  href: externalLinks.instagram
}, {
  icon: UsersIcon,
  label: 'Discord',
  value: 'Join the UCF BCM Discord',
  href: externalLinks.discord
}, {
  icon: MessageCircleIcon,
  label: 'Official UCF Organization Page',
  value: 'View UCF BCM on KnightConnect',
  href: externalLinks.knightConnect
}];
const visitingDetails = [{
  term: 'When',
  detail: 'Thursdays at 7:30 PM during the school year'
}, {
  term: 'Usual location',
  detail: 'Cape Florida Ballroom, Room 316, UCF Student Union third floor'
}, {
  term: 'Reminder',
  detail: 'Check the Events page or Instagram for the latest location.'
}];
export function Connect() {
  return <>
      <PageHeader eyebrow="Connect" title="Let’s get connected" intro="Interested in a community group, planning your first Thursday, or simply have a question? Share your information and a BCM leader can follow up." data-id="element-532" data-mp="330yu0ibwbl9r" />

      <Section className="relative overflow-hidden bg-[#fffdf6] py-14 sm:py-18" labelledBy="form-heading" data-id="element-533" data-mp="2tbfarbk9tped">
        <DoodleStar className="pointer-events-none absolute right-8 top-8 h-10 w-10" color="var(--bcm-yellow)" data-id="element-534" data-mp="2y8p4h3ep0n80" />
        <Container className="grid items-start gap-8 lg:grid-cols-[1.1fr_0.9fr] lg:gap-12" data-id="element-535" data-mp="3lyjktuy80tx0">
          <div className="rounded-[24px] border-2 border-brand-navy bg-white p-6 sm:p-8" data-id="element-536" data-mp="3qsw5277iu99t">
            <h2 id="form-heading" className="font-serif text-2xl font-semibold leading-snug text-brand-navy sm:text-3xl" data-id="element-537" data-mp="4fkxlj8bv5b2">
              Tell us how we can help
            </h2>
            <p className="mt-3 font-sans text-[16px] leading-relaxed text-ink" data-id="element-538" data-mp="3mnadrdke8eyg">
              The Get Connected form goes straight to the BCM leadership team. If the embedded form
              below does not load in your browser, use the button to open it in a new tab.
            </p>
            <ButtonExternal href={externalLinks.getConnectedForm} className="mt-6" data-id="element-539" data-mp="3cvnwazhgpjn9">
              Open the Get Connected Form
            </ButtonExternal>

            <div className="mt-7 overflow-hidden rounded-[20px] border-2 border-brand-navy bg-brand-tan" data-id="element-540" data-mp="330ciqkhsiih3">
              <iframe src={`${externalLinks.getConnectedForm}?embedded=true`} title="UCF BCM Get Connected form" loading="lazy" className="h-[520px] w-full border-0 bg-white" data-id="element-541" data-mp="2ywei25ctgzlx">
                Loading the Get Connected form…
              </iframe>
            </div>
            <p className="mt-3 font-meta text-[12.5px] text-ink" data-id="element-542" data-mp="1ih8z28nxiz5p">
              The form is hosted by Google. Nothing you type is stored by this website.
            </p>
          </div>

          <div data-id="element-543" data-mp="1ne920aphflsl">
            <h2 className="font-serif text-2xl font-semibold text-brand-navy" data-id="element-544" data-mp="qq2w7kgtqncq">Other ways to reach us</h2>
            <ul className="mt-5 space-y-4" data-id="element-545" data-mp="urem7r3m1alg">
              {contactCards.map((card, index) => <Reveal key={card.label} as="li" delay={stagger(index)} className="rounded-[20px] border-2 border-brand-navy bg-brand-tan p-5 transition-transform duration-200 hover:-translate-y-1" data-id="element-546" data-mp="zr0zu1ncosus" data-mpi={index}>
                  <div className="flex items-start gap-3" data-id="element-547" data-mp="c16o5nerpac4">
                    <card.icon aria-hidden="true" className="mt-0.5 h-5 w-5 shrink-0 text-brand-navy" data-id="element-548" data-mp="bzlstvkmpnd7" />
                    <div className="min-w-0" data-id="element-549" data-mp="2eod9yaaorzxs">
                      <p className="font-meta text-[11px] font-semibold uppercase tracking-[0.16em] text-brand-navy" data-id="element-550" data-mp="29p0s63yo9j22">
                        {card.label}
                      </p>
                      <a href={card.href} {...card.href.startsWith('mailto:') ? {} : {
                    target: '_blank',
                    rel: 'noopener noreferrer'
                  }} className="mt-1 inline-block break-words font-display text-base font-semibold text-brand-navy underline underline-offset-4 hover:no-underline" data-id="element-551" data-mp="2ni08272d9fa5">
                        {card.value}
                        {card.href.startsWith('mailto:') ? null : <NewTabHint data-id="element-552" data-mp="1c10gxerr96hp" />}
                      </a>
                    </div>
                  </div>
                </Reveal>)}
            </ul>

            <div className="mt-6 rounded-[20px] border-2 border-brand-navy bg-brand-blue p-5" data-id="element-553" data-mp="18181qj7uw23d">
              <h3 className="font-display text-xl font-bold text-brand-navy" data-id="element-554" data-mp="xw9tx5eqysxg">Visiting Thursday?</h3>
              <dl className="mt-3 space-y-3" data-id="element-555" data-mp="11wa6slm76w2e">
                {visitingDetails.map(item => <div key={item.term} data-id="element-556" data-mp="o5evz3s524w6">
                    <dt className="font-meta text-[11px] font-semibold uppercase tracking-[0.16em] text-brand-navy" data-id="element-557" data-mp="t4i44x3f0r8d">
                      {item.term}
                    </dt>
                    <dd className="mt-0.5 font-sans text-[15px] leading-relaxed text-ink" data-id="element-558" data-mp="e9u0zplhm4vx">
                      {item.detail}
                    </dd>
                  </div>)}
              </dl>
              <ButtonExternal href={externalLinks.studentUnion} variant="secondary" className="mt-5 w-full sm:w-auto" data-id="element-559" data-mp="a5w1vew5wu2v">
                Get Directions
              </ButtonExternal>
            </div>
          </div>
        </Container>
      </Section>
    </>;
}